import { Button } from "@components/button";
import Dialog from "@components/dialog/Dialog";
import { getCommonTableColumnsDefs, withDisplayName } from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { DocumentEntity, EntitySetName, IBankTransactionEntity } from "@odata/GeneratedEntityTypes";
import { BankTransactionTypeCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { PAIR_ACTION_ID } from "@pages/asset/PairingWithAssetTableView";
import DialogPage from "@pages/DialogPage";
import { isAccountAssignmentCompany } from "@utils/CompanyUtils";
import { getTextWidth } from "@utils/string";
import i18next from "i18next";
import React, { ReactElement } from "react";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { PageViewMode, Sort, TextAlign } from "../../../enums";
import { TValue } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import { formatCurrency } from "../../../types/Currency";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ITableViewBaseProps } from "../../../views/table/TableView";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { getDocumentStatusColumnDefinition } from "../../documents/DocumentCommonDefs";
import { getDocumentStatusFilterDefinition } from "../../documents/DocumentDef";
import {
    enhancedDocumentTypeByDDOPPFilterFormatter,
    getEnhancedDocumentTypeByDDOPPDef
} from "../../documents/proformaInvoices/ProformaInvoice.utils";
import { IDefinition } from "../../PageUtils";
import { DocumentStatusLocalPath, StatusEntities } from "../../reports/CommonDefs";
import {
    amountPaidFormatter,
    bankAmountDueFormatter,
    hasPairedChanged,
    IBankCustomData,
    savePairChanges
} from "./BankTransactions.utils";


interface IRenderPairingDialogArgs {
    view: React.ComponentType<ITableViewBaseProps>;
    type: BankTransactionTypeCode;
    saveDraftFn: () => void;
}

// this rendering methods are directly tied to this definition so we keep em out of utils to prevent circular dep.
// we may put them to separate plain utils file if needed
const displayPromptOnChange = (storage: FormStorage<unknown, IBankCustomData>, route = "") => {
    const pairedStorage = storage.getCustomData().pairedStorage;
    const pairedDocuments = pairedStorage.getCustomData().pairedDocuments || {};

    if (pairedStorage.tableAction === PAIR_ACTION_ID) {
        const hasChanges = hasPairedChanged(storage, pairedDocuments);
        if (hasChanges) {
            storage.setCustomData({
                promptDialogOpened: true,
                pairRedirectUrl: route,
                showSaveButton: !route
            });

            storage.refresh();
            return true;
        }
    }

    return false;
};


const handleClosePairing = (storage: FormStorage<unknown, IBankCustomData>) => {
    if (!displayPromptOnChange(storage)) {
        storage.setCustomData({ pairedDialogOpened: false });
        storage.refresh();
    }
};

const cancelPrompt = (storage: FormStorage<unknown, IBankCustomData>) => {
    storage.setCustomData({ promptDialogOpened: false });

    storage.refresh();
};


const finishClosingPrompt = (storage: FormStorage<unknown, IBankCustomData>) => {
    const url = storage.getCustomData().pairRedirectUrl;
    if (url) {
        storage.history.push(url);
    } else {
        storage.refresh();
    }
};

const savePairChangesAction = async (storage: FormStorage<unknown, IBankCustomData>, type: BankTransactionTypeCode, saveDraftFn?: () => void) => {
    const pairedStorage = storage.getCustomData().pairedStorage;
    const pairedDocuments = pairedStorage.getCustomData().pairedDocuments || {};

    await savePairChanges(storage, pairedDocuments, type);
    saveDraftFn?.();
    storage.setCustomData({
        promptDialogOpened: false,
        pairedDialogOpened: false
    });
    finishClosingPrompt(storage);
};

const cancelChanges = (storage: FormStorage<unknown, IBankCustomData>) => {
    storage.setCustomData({
        promptDialogOpened: false,
        pairedDialogOpened: false
    });
    finishClosingPrompt(storage);
};


export const renderPairingDialog = (storage: FormStorage<unknown, IBankCustomData>, args: IRenderPairingDialogArgs): ReactElement => {
    const showSave = storage.getCustomData().showSaveButton;
    const transaction = storage.data.entity as IBankTransactionEntity;
    const formattedAmount = formatCurrency(Math.abs(transaction.TransactionAmount), transaction.TransactionCurrency?.Code);
    // 2px border + 36px padding + edit icon 22px
    const amountColumnMaxWidth = `${Math.ceil(getTextWidth(formattedAmount, {
        weight: "bold",
        family: "Lato",
        size: "12px"
    })) + 63}px`;
    return (
        <>
            {storage.getCustomData().promptDialogOpened &&
                <>
                    <Dialog
                        onConfirm={showSave ? savePairChangesAction.bind(null, storage, args.type, args.saveDraftFn) : null}
                        onClose={cancelPrompt.bind(null, storage)}
                        isConfirmation
                        footer={<>
                            <Button isTransparent onClick={cancelPrompt.bind(null, storage)}>
                                {i18next.t("Common:General.Cancel")}
                            </Button>
                            <Button isTransparent onClick={cancelChanges.bind(null, storage)}>
                                {i18next.t("Common:Form.DiscardChanges")}
                            </Button>

                            {showSave &&
                                <Button
                                    onClick={savePairChangesAction.bind(null, storage, args.type, args.saveDraftFn)}>
                                    {i18next.t("Document:Form.SubmitChanges")}
                                </Button>
                            }
                        </>}>
                        {i18next.t("Common:General.UnsavedChanges")}
                    </Dialog>
                </>
            }
            {storage.getCustomData().pairedDialogOpened &&
                <Dialog
                    isEditableWindow={true}
                    onConfirm={null}
                    onClose={handleClosePairing.bind(null, storage)}>
                    <DialogPage
                        formProps={{
                            isSimple: true,
                            shouldHideVariant: false
                        }}
                        tableViewProps={{
                            saveDraftFn: args.saveDraftFn
                        }}
                        pageViewMode={PageViewMode.FormReadOnly}
                        rootStorage={storage}
                        tableView={args.view}
                        getDef={context => getDefinitions(context, args.type, amountColumnMaxWidth)}/>
                </Dialog>
            }
        </>
    );
};

export const getMasDocPairDef = (context: IAppContext): IDefinition => {
    return getCoreDef(context, null, false);
};

const getCoreDef = (context: IAppContext, type: BankTransactionTypeCode, useAmount: boolean, amountWidth?: string) => {
    const hasAccountAssignment = isAccountAssignmentCompany(context);

    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        isValueHelp: true,
        defaultFilters: [
            "DocumentType",
            "NumberOurs",
            "NumberTheirs",
            "TransactionAmount",
            "TransactionCurrency",
            "BusinessPartner",
            "ClearedStatus",
            "DateDue",
            "TransactionAmountToReceive",
            "TransactionAmountToPay",
            "SymbolVariable",
            DocumentStatusLocalPath
        ],
        filterDefinition: {
            ...getDocumentStatusFilterDefinition(StatusEntities),
            ...withDisplayName("DocumentType", {
                formatter: enhancedDocumentTypeByDDOPPFilterFormatter
            }),
            NumberOurs: {},
            ...(type === BankTransactionTypeCode.OutgoingPayment ? { NumberTheirs: {} } : {}), TransactionAmount: {},
            ...withDisplayName("BusinessPartner"),
            ...withDisplayName("TransactionCurrency", "Name"),
            "BusinessPartner/Street": {},
            "BusinessPartner/City": {},
            "BusinessPartner/PostalCode": {},
            "BusinessPartner/Country": {},
            "BusinessPartner/FirstName": {},
            "BusinessPartner/LastName": {},
            "BusinessPartner/Email": {},
            "BusinessPartner/PhoneNumber": {},
            TransactionAmountToPay: {},
            TransactionAmountToReceive: {},
            DateDue: {},
            SymbolVariable: {}
        }
    }];

    const table: ISplitPageTableDef = {
        filterBarDef,
        preventStoreVariant: true,
        id: "TransactionDocumentPairingSpecialTable",
        additionalProperties: [{ id: "TransactionCurrency" }, { id: "Currency" }, { id: "TransactionAmountToReceive" }, { id: "TransactionAmountToPay" },
            { id: "ExchangeRatePerUnit" }, { id: "Currency" }, { id: "Amount" }, { id: "TransactionAmount" }, { id: "IsTaxDocument" }, { id: "PostedStatus" }],
        columnDefinition: {
            NumberOurs: {},
            NumberTheirs: {},
            DateDue: {},
            PaymentMethodCode: {},
            SymbolVariable: {},
            "BankAccount/AccountNumber": {},
            "BankAccount/BankCode": {},
            TransactionAmountDue: {
                formatter: bankAmountDueFormatter
            },
            Note: { id: "Note" },
            ...getDocumentStatusColumnDefinition(),
            BusinessPartner: {
                id: "BusinessPartner",
                fieldSettings: {
                    displayName: "Name"
                }
            },
            ...getCommonTableColumnsDefs(),
            ...getEnhancedDocumentTypeByDDOPPDef(),
            DateIssued: {}
        },
        columns: [
            DocumentEntity.DocumentType,
            DocumentEntity.NumberOurs,
            DocumentEntity.TransactionAmountDue,
            ...(type === BankTransactionTypeCode.OutgoingPayment ? [DocumentEntity.NumberTheirs] : []),
            DocumentStatusLocalPath,
            DocumentEntity.SymbolVariable,
            DocumentEntity.BusinessPartner,
            DocumentEntity.Note
        ],
        initialSortBy: [{ id: DocumentEntity.DateCreated, sort: Sort.Desc }],
        title: i18next.t("Document:Pair.PairedDocument")
    };

    if (useAmount) {
        table.columnDefinition = {
            ...table.columnDefinition,
            [BindingContext.localContext("AmountPaid")]: {
                label: i18next.t("Banks:Pairing.AmountPaid"),
                textAlign: TextAlign.Right,
                width: amountWidth,
                fieldSettings: {
                    disableSort: true
                },
                formatter: amountPaidFormatter
            }
        };

        table.columns.splice(2, 0, BindingContext.localContext("AmountPaid"));
    }

    if (hasAccountAssignment) {
        table.filterBarDef[0].defaultFilters.push("DateAccountingTransaction");
        table.filterBarDef[0].filterDefinition = {
            ...table.filterBarDef[0].filterDefinition,
            DateAccountingTransaction: {}
        };

        table.additionalProperties = [
            ...table.additionalProperties,
            { id: "AccountAssignment" },
            { id: "AccountAssignment/ShortName" },
            { id: "AccountAssignment/CreditAccount/Id" },
            { id: "AccountAssignment/DebitAccount/Id" },
            { id: "AccountAssignment/CreditAccount/Name" },
            { id: "AccountAssignment/CreditAccount/Number" },
            { id: "AccountAssignment/DebitAccount/Name" },
            { id: "AccountAssignment/DebitAccount/Number" },
            { id: "DateAccountingTransaction" }
        ];

        table.columnDefinition = {
            ...table.columnDefinition,
            DateAccountingTransaction: { id: "DateAccountingTransaction" },
            AccountAssignment: {
                additionalProperties: [{ id: "ShortName" }, { id: "CreditAccount/Id" }, { id: "DebitAccount/Id" }, { id: "CreditAccount/Name" }, { id: "CreditAccount/Number" }, { id: "DebitAccount/Name" }, { id: "DebitAccount/Number" }],
                formatter: (val: TValue, args: IFormatOptions) => {
                    const value = args.item.AccountAssignment?.ShortName;
                    return {
                        value: <>{value}</>,
                        tooltip: value
                    };
                }
            }
        };

        table.columns = [
            "DateAccountingTransaction",
            ...table.columns
        ];
    }

    return {
        entitySet: EntitySetName.PayingDocuments,
        table
    };

};

export const getDefinitions = (context: IAppContext, type: BankTransactionTypeCode, amountWidth: string): IDefinition => {
    return getCoreDef(context, type, true, amountWidth);
};